import React from 'react';
import { useData, useField } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { ViewLayout } from '../EpcrFields';

export function EpcrViewVitals() {
  const [tab, setTab] = React.useState("general");
  const data = useData();

  const vitals = useField("eVitals/eVitals.VitalGroup") || [];
  const sorted = [...vitals].sort((a, b) => (a["eVitals.01"] || "").localeCompare(b["eVitals.01"] || ""));

  const time = (iso) => iso ? new Date(iso).toTimeString().slice(0, 5) : "—";
  const dateStr = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  const sections = sorted.map((v, i) => ({
    id: `set-${i + 1}`,
    label: `Set #${i + 1} — ${time(v["eVitals.01"])}`,
  }));

  const subbar = (
    <div className="vitals-sub">
      {["general", "scoring", "ecg"].map(t => (
        <button key={t} className={tab === t ? "active" : ""} onClick={() => setTab(t)}>
          {t.toUpperCase()}
        </button>
      ))}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, padding: "0 0 10px" }}>
        <span className="status-pill"><span className="dt" /> {sorted.length} sets recorded</span>
      </div>
    </div>
  );

  const addBar = (
    <div className="add-bar">
      <button className="btn-primary"><Icons.plus size={14} /> Add vitals</button>
      <button className="btn-secondary"><Icons.pulse size={14} /> Pull from monitor</button>
    </div>
  );

  return (
    <ViewLayout sections={sections} subbar={subbar} addBar={addBar}>
      <div className="vitals-list">
        {sorted.map((v, i) => (
          <div className="vital-card" id={`set-${i + 1}`} data-section={`set-${i + 1}`} key={i} style={{ scrollMarginTop: 130 }}>
            <div className="vital-time">
              <div className="hh">{time(v["eVitals.01"])}</div>
              <div className="dt">{dateStr(v["eVitals.01"])}</div>
              <div className="seq">Set #{i + 1}</div>
            </div>
            {tab === "general" && <VitalCellsGeneral v={v} />}
            {tab === "scoring" && <VitalCellsScoring v={v} data={data} />}
            {tab === "ecg" && <VitalCellsEcg v={v} data={data} />}
            <div className="vital-actions">
              <button className="icon-btn"><Icons.edit size={14} /></button>
              <button className="icon-btn"><Icons.more size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </ViewLayout>
  );
}

function VitalCellsGeneral({ v }) {
  const bpg = v["eVitals.BloodPressureGroup"] || {};
  const hrg = v["eVitals.HeartRateGroup"] || {};
  const tmp = v["eVitals.TemperatureGroup"] || {};
  const gcs = v["eVitals.GlasgowScoreGroup"] || {};
  const sbp = bpg["eVitals.06"];
  const dbp = bpg["eVitals.07"];
  const map = bpg["eVitals.09"];
  const hr = hrg["eVitals.10"];
  const spo2 = v["eVitals.12"];
  const rr = v["eVitals.14"];
  const bp = sbp && dbp ? `${sbp}/${dbp}` : "—";
  const hrFlag = hr && (hr > 120 || hr < 50);
  const rrFlag = rr && (rr > 24 || rr < 8);
  return (
    <div className="vital-cells">
      <Cell label="BP" value={bp} unit="mmHg" />
      <Cell label="MAP" value={map} unit="mmHg" />
      <Cell label="HR" value={hr} unit="bpm" flag={hrFlag} />
      <Cell label="SpO₂" value={spo2} unit="%" />
      <Cell label="RR" value={rr} unit="/min" flag={rrFlag} />
      <Cell label="Pain" value={v["eVitals.27"] || "—"} />
      <Cell label="Temp" value={tmp["eVitals.24"]} unit="°C" />
      <Cell label="ETCO₂" value={v["eVitals.16"]} unit="mmHg" />
      <Cell label="BGL" value={v["eVitals.18"]} unit="mg/dL" flag={v["eVitals.18"] && (v["eVitals.18"] < 70 || v["eVitals.18"] > 250)} />
      <Cell label="GCS" value={gcs["eVitals.23"]} flag={gcs["eVitals.23"] < 13} />
      <Cell label="Stroke" value={v["eVitals.33"]} unit="CPSS" />
    </div>
  );
}

function VitalCellsScoring({ v, data }) {
  const g = v["eVitals.GlasgowScoreGroup"] || {};
  return (
    <div className="vital-cells">
      <Cell label="GCS-E" value={g["eVitals.19"]} unit="/4" />
      <Cell label="GCS-V" value={g["eVitals.20"]} unit="/5" />
      <Cell label="GCS-M" value={g["eVitals.21"]} unit="/6" />
      <Cell label="GCS Total" value={g["eVitals.23"]} unit="/15" flag={g["eVitals.23"] < 13} />
      <Cell label="GCS Qual" value={data.label("eVitals.22", g["eVitals.22"]) || "—"} />
      <Cell label="Stroke (CPSS)" value={v["eVitals.33"]} />
    </div>
  );
}

function VitalCellsEcg({ v, data }) {
  const cg = v["eVitals.CardiacRhythmGroup"] || {};
  return (
    <div className="vital-cells" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
      <Cell label="Lead Count" value={data.label("eVitals.03", cg["eVitals.03"]) || "—"} />
      <Cell label="Type" value={data.label("eVitals.04", cg["eVitals.04"]) || "—"} />
      <Cell label="Method" value={data.label("eVitals.05", cg["eVitals.05"]) || "—"} />
    </div>
  );
}

function Cell({ label, value, unit, flag }) {
  const empty = value == null || value === "" || value === "—";
  return (
    <div className="vc">
      <div className="lbl">{label}</div>
      <div className={"val" + (flag ? " flag" : "")}>
        {empty ? <span style={{ color: "var(--epcr-ink-4)" }}>—</span> : value}
        {!empty && unit && <span className="unit"> {unit}</span>}
      </div>
    </div>
  );
}
